import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { apiClient } from "../../api/apiClient";

const UNCATEGORIZED = "Uncategorized";

/* ── Icons ───────────────────────────────────────────────────────────────── */
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

/* ── Skeleton row ────────────────────────────────────────────────────────── */
function SkeletonRow() {
  return (
    <div className="animate-pulse flex items-center gap-3 rounded-xl px-3 py-3">
      <div className="h-9 w-9 rounded-lg bg-mainDark/8 shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3 w-2/5 rounded-full bg-mainDark/8" />
      </div>
    </div>
  );
}

/* ── Modal ───────────────────────────────────────────────────────────────── */
export function ProjectsModal({ bookId, isOpen, onClose }) {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newProjectName, setNewProjectName] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadProjects = useCallback(async () => {
    if (!bookId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/project");
      setProjects(res.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.message || t("projectsModal.loadErrorDefault"));
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [bookId, t]);

  useEffect(() => {
    if (isOpen && bookId) loadProjects();
  }, [isOpen, bookId, loadProjects]);

  /* close create row when modal closes */
  useEffect(() => {
    if (!isOpen) { setCreating(false); setNewProjectName(""); setError(""); }
  }, [isOpen]);

  const bookInProject = (project) =>
    (project.books || []).some((id) => String(id) === String(bookId));

  const addToProject = async (projectId) => {
    if (!bookId) return;
    setBusy(true);
    setError("");
    try {
      await apiClient.post(`/project/${projectId}/book`, { bookId });
      toast.success(t("projectsModal.savedToast"));
      onClose?.();
    } catch (e) {
      setError(e.response?.data?.message || t("projectsModal.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const createAndSave = async (e) => {
    e.preventDefault();
    const name = newProjectName.trim();
    if (!name || name === UNCATEGORIZED) {
      setError(t("projectsModal.invalidName"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const createRes = await apiClient.post("/project", { name });
      const created = createRes.data?.data;
      if (created?._id) {
        await apiClient.post(`/project/${created._id}/book`, { bookId });
        setNewProjectName("");
        toast.success(t("projectsModal.savedToast"));
        onClose?.();
      } else {
        setError(t("projectsModal.saveReferenceError"));
      }
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.response?.data?.data ||
          t("projectsModal.createFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="projects-modal-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ animation: "modalBackdropIn 0.2s ease both" }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/35 backdrop-blur-sm" onClick={onClose} />

      {/* Card */}
      <div
        className="relative w-full max-w-md bg-mainWhite rounded-2xl shadow-search overflow-hidden"
        style={{ animation: "modalCardIn 0.22s cubic-bezier(0.22,1,0.36,1) both" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <h2 id="projects-modal-title" className="text-lg font-normal text-mainDark">
            {t("projectsModal.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl text-mainDark/30 hover:text-mainDark hover:bg-mainDark/6 transition-all duration-150"
            aria-label={t("common.close")}
          >
            <span className="block h-4 w-4"><XIcon /></span>
          </button>
        </div>

        <div className="h-px bg-mainDark/8 mx-5" />

        {/* Error */}
        {error && (
          <p className="mx-5 mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-500">
            {String(error)}
          </p>
        )}

        {/* Project list */}
        <div className="px-2 pt-2 pb-2 max-h-[45vh] overflow-y-auto">
          {loading ? (
            <div>
              {[0, 1, 2].map((i) => <SkeletonRow key={i} />)}
            </div>
          ) : (
            <ul className="space-y-0.5">
              {projects.map((p) => {
                const saved = bookInProject(p);
                const initial = p.isDefault ? "★" : (p.name?.[0] || "?").toUpperCase();
                return (
                  <li key={p._id}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => addToProject(p._id)}
                      className={`group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all duration-150 disabled:opacity-50
                        ${saved
                          ? "bg-mainBlue/10"
                          : "hover:bg-mainDark/5"
                        }`}
                    >
                      {/* Avatar */}
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold select-none transition-all duration-150
                        ${saved
                          ? "bg-mainBlue text-mainDark"
                          : "bg-mainDark/8 text-mainDark/50 group-hover:bg-mainDark/12 group-hover:text-mainDark/80"
                        }`}
                      >
                        {saved ? <span className="h-4 w-4"><CheckIcon /></span> : initial}
                      </div>

                      {/* Name */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-normal truncate transition-colors duration-150
                          ${saved ? "text-mainDark" : "text-mainDark/70 group-hover:text-mainDark"}`}
                        >
                          {p.isDefault ? t("common.uncategorizedProjectName") : p.name}
                        </p>
                        {p.isDefault && (
                          <p className="text-[11px] text-mainDark/35 font-light">
                            {t("projectsModal.defaultBadge")}
                          </p>
                        )}
                      </div>

                      {/* Saved indicator */}
                      {saved && (
                        <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-mainBlue">
                          {t("projectsModal.savedAriaLabel")}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Create new project */}
        <div className="border-t border-mainDark/8 px-3 py-3">
          {creating ? (
            <form onSubmit={createAndSave} className="flex items-center gap-2">
              <input
                autoFocus
                className="flex-1 min-w-0 rounded-xl border border-mainDark/15 bg-mainWhite px-3 py-2 text-sm text-mainDark placeholder-mainDark/30 outline-none focus:border-mainBlue transition-colors duration-150"
                placeholder={t("projectsModal.projectNamePlaceholder")}
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                disabled={busy}
              />
              <button
                type="submit"
                disabled={busy || loading}
                className="shrink-0 px-3 py-2 rounded-xl bg-mainDark text-mainWhite text-sm font-normal hover:bg-mainBlue hover:text-mainDark transition-all duration-150 disabled:opacity-50"
              >
                {t("projectsModal.createAndSave")}
              </button>
              <button
                type="button"
                onClick={() => { setCreating(false); setNewProjectName(""); }}
                className="shrink-0 p-2 rounded-xl text-mainDark/30 hover:text-mainDark transition-colors duration-150"
              >
                <span className="block h-4 w-4"><XIcon /></span>
              </button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-mainDark/40 hover:text-mainDark hover:bg-mainDark/5 transition-all duration-150"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-mainDark/6 text-mainDark/40">
                <span className="block h-4 w-4"><PlusIcon /></span>
              </span>
              <span className="text-sm font-light">{t("projectsModal.createNewProject")}</span>
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes modalBackdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes modalCardIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default ProjectsModal;
